import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Product, ProductCollection } from '../types/product';

const SYNC_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours


/**
 * Converts absolute backend logo URLs to relative paths so they go through
 * the Vite dev proxy and the Service Worker in production.
 * e.g. "http://backend.test/storage/logos/foo.png" → "/storage/logos/foo.png"
 */
function toRelativeLogo(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

function normalizeLogos(products: Product[]): Product[] {
  return products.map((p) => ({
    ...p,
    logoUrl: toRelativeLogo(p.logoUrl),
    partner: p.partner
      ? { ...p.partner, logoUrl: toRelativeLogo(p.partner.logoUrl) }
      : p.partner,
  }));
}

/**
 * Pre-fetches logo URLs so they end up in the Service Worker cache.
 */
function prefetchLogos(products: Product[]) {
  const urls = new Set<string>();
  for (const p of products) {
    if (p.logoUrl) urls.add(p.logoUrl);
    if (p.partner?.logoUrl) urls.add(p.partner.logoUrl);
  }
  for (const url of urls) {
    fetch(url).catch(() => {});
  }
}

/**
 * Fetches the complete product catalog in a single request.
 * This is the single source of truth for all product data.
 * Background-syncs every 4 hours when online.
 */
export function useProductCatalog(enabled = true) {
  return useQuery<Product[]>({
    queryKey: ['product-catalog'],
    queryFn: async () => {
      const data = await api<ProductCollection>('/products?itemsPerPage=500');
      const products = normalizeLogos(data.member);
      prefetchLogos(products);
      return products;
    },
    enabled,
    // Catalog data may refetch once it is older than the sync interval —
    // overrides the global Infinity default, which stays in place for the
    // mutation-backed queries (VIVATISPDB-305). Stale data is still rendered;
    // going stale never discards anything (VIVATISPDB-435).
    staleTime: SYNC_INTERVAL,
    // Covers the PWA being pulled out of the background without a cold start:
    // refetches on focus only when the snapshot is older than SYNC_INTERVAL.
    refetchOnWindowFocus: true,
    // Only poll while online — see VIVATISPDB-305.
    refetchInterval: () =>
      typeof navigator !== 'undefined' && navigator.onLine ? SYNC_INTERVAL : false,
    refetchIntervalInBackground: true,
    // Sync as soon as the connection comes back, regardless of staleTime.
    refetchOnReconnect: 'always',
  });
}
